import type { PortfolioCollectionPlatform } from "@repo/shared/types";
import type { Localize } from "@/lib/i18n/use-localize";

import { platformLabel } from "./platform-label";
import type { WeightsGridColumn } from "./use-weights-grid-autosave";

type GridPortfolio = { id: string; riskScore: number; platformIds?: string[] };

type ColumnContext = {
  /** Labels of the platforms the portfolio is assigned to. */
  platformNames: string[];
  /** Whether its risk score has other portfolios, so the column needs telling apart. */
  hasSiblings: boolean;
};

/**
 * The grid columns of every portfolio, grouped by risk score. Within a score
 * the portfolio without a platform comes first, the rest by platform name.
 */
export function groupColumnsByScore<T extends GridPortfolio>(
  portfolios: T[],
  platforms: Pick<PortfolioCollectionPlatform, "id" | "labelI18n">[],
  localize: Localize,
  toColumn: (portfolio: T, context: ColumnContext) => WeightsGridColumn,
): Map<number, WeightsGridColumn[]> {
  const byScore = new Map<number, { portfolio: T; platformNames: string[] }[]>();
  for (const portfolio of portfolios) {
    const entry = {
      portfolio,
      platformNames: (portfolio.platformIds ?? []).map((id) =>
        platformLabel(platforms, id, localize),
      ),
    };
    const group = byScore.get(portfolio.riskScore);
    if (group) group.push(entry);
    else byScore.set(portfolio.riskScore, [entry]);
  }

  const columns = new Map<number, WeightsGridColumn[]>();
  for (const [score, group] of byScore) {
    group.sort(
      (a, b) =>
        Number(a.platformNames.length > 0) - Number(b.platformNames.length > 0) ||
        a.platformNames.join(", ").localeCompare(b.platformNames.join(", ")) ||
        a.portfolio.id.localeCompare(b.portfolio.id),
    );
    columns.set(
      score,
      group.map(({ portfolio, platformNames }) =>
        toColumn(portfolio, { platformNames, hasSiblings: group.length > 1 }),
      ),
    );
  }
  return columns;
}
